'use client'
import {createSlice} from "@reduxjs/toolkit";
import {getUserInfoAsync, getUserInfoGameBaseAsync, userLoginAsync, visitorLoginAsync} from "@/app/userSliceAPI";
import {RootState} from "@/app/store";
import Util from "@/Master/Util";

export enum RewardType {
    GC = 1,
    SC = 2,
}

export enum LoginType {
    visitor = 1, //游客
    normal = 2, //普通用户
}

export interface UserInfoState {
    "token": string,   //用户token
    "userId": number,  //用户id
    "tokenExpired": number,  //用户token失效时间戳
    "isLoggedOut": boolean,  //是否已登出
    "loginType": LoginType,  //登录类型
    "zipCode": string,  //邮编
    "areaCode": string,  //区号
    "mobile": string,   //手机号
    "nickname": string,  //昵称
    "rewardType": RewardType,    //奖励类型
    "isGuide": boolean,  //是否新手引导
    "guideOpen": boolean, //是否开启新手引导
    "isAddDesktopOpen": boolean, //是否打开过添加桌面弹窗
    "addDesktopOpen": boolean, //是否添加桌面
    "addDesktopRewards": number, //添加桌面奖励sc
    "addDesktopRewardsGC":number,//添加桌面奖励gc
    "availableScAmount": number,    //可提现sc余额
    "balanceVersion": number,   //余额版本
    "convertAvailableScAmount": number, //可提现sc余额(万分位)
    "convertRewardScAmount": number,    //赠送sc余额(不可提现)(万分位)
    "convertTotalScAmount": number, //总sc余额
    "gameExp": number,  //游戏经验
    "gameLevel": number,    //游戏等级
    "gcAmount": number, //gc余额
    "rewardScAmount": number,    //赠送sc余额(不可提现)
    "starterPackFlag": boolean,   //是否充值过首充礼包
    "starterPackFlagOpen": boolean,   //是否打开过首充礼包
    "specialPackFlagOpen": boolean,   //是否打开过特价礼包
    "vipExp": number,    //vip经验
    "vipLevel": number,  //vip等级
    "firstRequestUserInfo": boolean,
    "requestStatus": 'idle' | 'pending'  | 'failed',  //请求状态

}

export const initialUserInfoState: UserInfoState = {
    token: '',
    userId: 0,
    tokenExpired: 0,
    isLoggedOut: false,
    zipCode: "",
    loginType: LoginType.visitor,
    areaCode: "",
    mobile: "",
    nickname: "",
    rewardType: RewardType.GC,
    isGuide: false,
    guideOpen: false,
    isAddDesktopOpen: false,
    addDesktopOpen: false,
    addDesktopRewards: 0,
    addDesktopRewardsGC:0,
    availableScAmount: 0,
    balanceVersion: 0,
    convertAvailableScAmount: 0,
    convertRewardScAmount: 0,
    convertTotalScAmount: 0,
    gameExp: 0,
    gameLevel: 0,
    gcAmount: 0,
    rewardScAmount: 0,
    starterPackFlag: true,
    starterPackFlagOpen: false,
    specialPackFlagOpen: false,
    vipExp: 0,
    vipLevel: 0,
    firstRequestUserInfo: true,
    requestStatus: 'pending',
}

export const UserInfoSlice = createSlice({
    name: "userInfo",
    initialState: initialUserInfoState,
    reducers: {
        SET_ACCESS_TOKEN: (state, action) => {
            state.token = action.payload;
        },
        SET_USER_ID: (state, action) => {
            state.userId = action.payload;
        },
        SET_USER_LOGIN_TYPE: (state, action) => {
            state.loginType = action.payload;
        },
        SET_REWARD_TYPE: (state, action) => {
            state.rewardType = action.payload;
        },
        SET_GUIDE_OPEN: (state, action) => {
            state.guideOpen = action.payload;
        },
        SET_TOKEN_EXPIRED: (state, action) => {
            state.tokenExpired = action.payload;
        },
        SET_NICK_NAME: (state, action) => {
            state.nickname = action.payload;
        },
        SET_IS_ADD_DESKTOP_OPEN: (state, action) => {
            state.isAddDesktopOpen = action.payload;
        },
        SET_ADD_DESKTOP_OPEN: (state, action) => {
            state.addDesktopOpen = action.payload;
        },
        SET_ADD_DESKTOP_REWARDS: (state, action) => {
            state.addDesktopRewards = action.payload;
        },
        SET_ADD_DESKTOP_REWARDS_GC: (state, action) => {
            state.addDesktopRewardsGC = action.payload;
        },
        SET_IS_LOGGED_OUT: (state, action) => {
            state.isLoggedOut = action.payload;
        },
        SET_STARTER_PACK_FLAG_OPEN: (state, action) => {
            state.starterPackFlagOpen = action.payload;
        },
        SET_SPECIAL_PACK_FLAG_OPEN: (state, action) => {
            state.specialPackFlagOpen = action.payload;
        },
        updateUserInfo: (state, action) => {
            if (Util.isLog){
                console.log('updateUserInfo', action.payload)
            }
            state = action.payload;
        },
        RESET_USER_INFO: (state) => {
            return initialUserInfoState;
        }
    },
    extraReducers: builder => {
        // 注册用户登录
        builder
            .addCase(userLoginAsync.pending, state => {
                state.requestStatus = 'pending';
            })
            .addCase(userLoginAsync.fulfilled, (state, action) => {
                state.requestStatus = 'idle';
                state.token = action.payload.token;
                state.userId = action.payload.id;
                state.loginType = LoginType.normal;
                state.tokenExpired = action.payload.expireTime;
            })
            .addCase(userLoginAsync.rejected, state => {
                state.requestStatus = 'failed';
            })
        // 游客登陆
        builder
            .addCase(visitorLoginAsync.pending, state => {
                state.requestStatus = 'pending';
            })
            .addCase(visitorLoginAsync.fulfilled, (state, action) => {
                state.requestStatus = 'idle';
                if (Util.isLog){
                    console.log(action.payload);
                }
                state.token = action.payload.token;
                state.userId = action.payload.id;
                state.tokenExpired = action.payload.expireTime;
                state.loginType = LoginType.visitor;
            })
            .addCase(visitorLoginAsync.rejected, state => {
                state.requestStatus = 'failed';
            })
        // 获取用户信息
        builder
            .addCase(getUserInfoAsync.pending, state => {
                state.requestStatus = 'pending';
            })
            .addCase(getUserInfoAsync.fulfilled, (state, action) => {
                state.firstRequestUserInfo = false;
                state.requestStatus = 'idle';
                state.availableScAmount = action.payload.baseInfo.availableScAmount;
                state.balanceVersion = action.payload.baseInfo.balanceVersion;
                state.convertAvailableScAmount = action.payload.baseInfo.convertAvailableScAmount;
                state.convertRewardScAmount = action.payload.baseInfo.convertRewardScAmount;
                state.convertTotalScAmount = action.payload.baseInfo.convertTotalScAmount;
                state.gameExp = action.payload.baseInfo.gameExp;
                state.gameLevel = action.payload.baseInfo.gameLevel;
                state.gcAmount = action.payload.baseInfo.gcAmount;
                state.rewardScAmount = action.payload.baseInfo.rewardScAmount;
                state.vipExp = action.payload.baseInfo.vipExp;
                state.vipLevel = action.payload.baseInfo.vipLevel;
                state.starterPackFlag = action.payload.gameRewardInfo.starterPackFlag;
                state.nickname = action.payload.userInfo.nickname;
                state.zipCode = action.payload.userInfo.zipCode;
                state.isGuide = action.payload.gameRewardInfo.rewardRecord.starter || false;
                state.addDesktopOpen = action.payload.gameRewardInfo.rewardRecord.add_table;
                state.mobile = action.payload.userInfo.mobile;
                state.areaCode = action.payload.userInfo.areaCode;
            })
           .addCase(getUserInfoAsync.rejected, state => {
                state.requestStatus = 'failed';
            })
        // 获取用户信息2
        builder
           .addCase(getUserInfoGameBaseAsync.pending, state => {
                state.requestStatus = 'pending';
            })
           .addCase(getUserInfoGameBaseAsync.fulfilled, (state, action) => {
               state.requestStatus = 'idle';
               state.availableScAmount = action.payload.baseInfo.availableScAmount;
               state.balanceVersion = action.payload.baseInfo.balanceVersion;
               state.convertAvailableScAmount = action.payload.baseInfo.convertAvailableScAmount;
               state.convertRewardScAmount = action.payload.baseInfo.convertRewardScAmount;
               state.convertTotalScAmount = action.payload.baseInfo.convertTotalScAmount;
               state.gameExp = action.payload.baseInfo.gameExp;
               state.gameLevel = action.payload.baseInfo.gameLevel;
               state.gcAmount = action.payload.baseInfo.gcAmount;
               state.rewardScAmount = action.payload.baseInfo.rewardScAmount;
           })
    }
})

export const {
    SET_ACCESS_TOKEN,
    SET_USER_ID,
    SET_USER_LOGIN_TYPE,
    SET_REWARD_TYPE,
    SET_GUIDE_OPEN,
    SET_TOKEN_EXPIRED,
    SET_NICK_NAME,
    SET_IS_ADD_DESKTOP_OPEN,
    SET_ADD_DESKTOP_OPEN,
    SET_ADD_DESKTOP_REWARDS,
    SET_ADD_DESKTOP_REWARDS_GC,
    SET_IS_LOGGED_OUT,
    SET_STARTER_PACK_FLAG_OPEN,
    SET_SPECIAL_PACK_FLAG_OPEN,
    updateUserInfo,
    RESET_USER_INFO,
} = UserInfoSlice.actions;

export const selectUserInfo = (state: RootState) => state.userInfo;

export default UserInfoSlice.reducer;

